"use client";

import { Mail, Phone, MapPin, Instagram, Linkedin, Loader, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ConversationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-16 px-8">
        <div className="size-20 border border-primary flex items-center justify-center mb-8 rounded-md">
          <Check className="size-10 text-primary" strokeWidth={1.25} />
        </div>
        <p className="font-default text-2xl font-light uppercase tracking-tight text-foreground mb-4">
          Transmission Received
        </p>
        <p className="font-sans text-base text-muted-foreground max-w-sm mb-8">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-mono text-[10px] uppercase tracking-widest font-medium border-border text-foreground hover:bg-primary hover:text-primary-foreground rounded-md">
          New Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
                Full Name *
              </FormLabel>
              <FormControl>
                <Input placeholder="Jordan Rivers" {...field} className="border-0 border-b border-border bg-transparent px-0 h-11 font-sans text-base text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
                Email Address *
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="casting@agency.com" {...field} className="border-0 border-b border-border bg-transparent px-0 h-11 font-sans text-base text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary rounded-md" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
                Contact Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 212-555-0199" {...field} className="border-0 border-b border-border bg-transparent px-0 h-11 font-sans text-base text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary rounded-md" />
              </FormControl>
              <FormDescription className="font-mono text-[10px] text-muted-foreground/50 uppercase">
                Direct line for urgent casting calls.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="project_type" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
                Inquiry Type *
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className="border-0 border-b border-border bg-transparent px-0 h-11 font-sans text-base text-foreground focus-visible:ring-0 focus-visible:border-primary w-full rounded-md">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-popover border-border rounded-md">
                  <SelectItem value="Film" className="font-sans">Film / Feature</SelectItem>
                  <SelectItem value="Television" className="font-sans">Television / Series</SelectItem>
                  <SelectItem value="Theater" className="font-sans">Theater / Stage</SelectItem>
                  <SelectItem value="Commercial" className="font-sans">Commercial / Voice</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="budget" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
              Production Timeline
            </FormLabel>
            <FormControl>
              <Input type="text" placeholder="e.g., Q4 2024" {...field} className="border-0 border-b border-border bg-transparent px-0 h-11 font-sans text-base text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary rounded-md" />
            </FormControl>
            <FormDescription className="font-mono text-[10px] text-muted-foreground/50 uppercase">
              Specify shooting dates or rehearsal schedules.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
              Project Details *
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Briefly describe the role, script overview, or collaboration proposal..." rows={6} {...field} className="border-0 border-b border-border bg-transparent px-0 py-3 font-sans text-base text-foreground placeholder:text-muted-foreground/30 focus-visible:ring-0 focus-visible:border-primary resize-none rounded-md" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/30 bg-destructive/10 rounded-md">
          <p className="font-sans text-sm text-destructive">{submitError}</p>
        </div>}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground order-2 sm:order-1">
          Response via Representation: 24-48h
        </p>
        <Button type="submit" disabled={isSubmitting} className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-10 shadow-md order-1 sm:order-2 w-full sm:w-auto sm:min-w-[200px] rounded-md transition-all hover:tracking-[0.25em]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Transmitting
            </> : <>
              Secure Talent
              <ArrowRight className="ml-2 size-4" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function GetInTouch() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const contactDetails = [{
    icon: "mail",
    label: "contact@jordanrivers.actor",
    href: "mailto:contact@jordanrivers.actor",
    underline: true,
    accent: true
  }, {
    icon: "phone",
    label: "+1 (212) 555-0199",
    href: "tel:+12125550199",
    underline: false,
    accent: false
  }, {
    icon: "map",
    label: "Manhattan, New York / London, UK",
    href: "",
    underline: false,
    accent: false
  }];
  const socialLinks = [{
    name: "Instagram",
    icon: "instagram",
    to: "#"
  }, {
    name: "LinkedIn",
    icon: "linkedin",
    to: "/RepresentationPage"
  }];
  return <section data-section-id="3524" id="get-in-touch" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-40 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 xl:gap-20">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2 className="font-default font-light text-foreground leading-[1.0] mb-10 text-5xl sm:text-6xl md:text-7xl lg:text-[88px]">
              <span className="block">Let's</span>
              <span className="block ml-3 md:ml-6">Frame</span>
              <span className="block ml-6 md:ml-12">The</span>
              <span className="block ml-9 md:ml-[72px]">
                Vision<span className="text-primary">.</span>
              </span>
            </h2>

            <p className="font-sans text-lg text-muted-foreground leading-relaxed max-w-sm mb-12">
              For casting inquiries, script submissions, or collaboration requests, please use the secure portal or contact representation directly.
            </p>

            <div className="space-y-5 mb-12">
              {contactDetails.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-4">
                  <span className="text-primary mt-1 shrink-0">
                    {item.icon === "mail" && <Mail className="size-4" strokeWidth={1.25} />}
                    {item.icon === "phone" && <Phone className="size-4" strokeWidth={1.25} />}
                    {item.icon === "map" && <MapPin className="size-4" strokeWidth={1.25} />}
                  </span>
                  {item.href ? <Link to={item.href} className={`font-sans text-[17px] ${item.accent ? "text-primary" : "text-foreground"} ${item.underline ? "underline underline-offset-4 decoration-1" : ""} hover:text-primary transition-colors`}>
                      {item.label}
                    </Link> : <span className="font-sans text-[17px] text-foreground">{item.label}</span>}
                </div>)}
            </div>

            <div className="flex items-center gap-6">
              {socialLinks.map((social, i) => <Link key={social.name} data-index={i} to={social.to} aria-label={social.name} className="text-foreground hover:text-primary transition-colors">
                  {social.icon === "instagram" && <Instagram className="size-7" strokeWidth={1.25} />}
                  {social.icon === "linkedin" && <Linkedin className="size-7" strokeWidth={1.25} />}
                </Link>)}
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-1000 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative">
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-primary z-10" />

              <div className="relative border border-border bg-card p-8 md:p-12 lg:p-16 shadow-md rounded-md">
                <FormProvider formKey="contact_form_representation" formId="contact_form_representation" sectionName="start-the-conversation" defaultValues={{
                name: "",
                email: "",
                phone: "",
                project_type: "",
                budget: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {},
                project_type: {
                  required: true
                },
                budget: {},
                message: {
                  required: true
                }
              }} submitText="Secure Talent" successMessage="Your inquiry has been logged. Jordan's representation will review and respond within 48 hours." errorMessage="Transmission error. Please verify your connection or contact representation directly via email.">
                  <ConversationForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}